import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Image, Modal } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { API_URI } from "../Common/data";
import Loader from "../loader/Loader";
import userAvatar from "../Assets/ab4.png";
import BookingHistory from "./BookingHistory";
import UserQueries from "./UserQueries";

function Profile() {
  const [profileData, setProfileData] = useState({
    name: "John Doe",
    email: "johndoe@example.com",
    mobile: "1234567890",
    profileImage: ""
  });
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showQueryModal, setShowQueryModal] = useState(false);
  const [queryData, setQueryData] = useState({ subject: "", description: "" });
  const [activeTab, setActiveTab] = useState("bookings");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URI}/user/self`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("cine_userToken")}`
          }
        });

        if (response.status === 200) {
          setProfileData(response?.data?.data);
        }
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to fetch profile data!"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await axios.put(`${API_URI}/user/update-self`, profileData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("cine_userToken")}`
        }
      });
      toast.success("Profile updated successfully!");
      setShowEditModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update profile!");
    } finally {
      setLoading(false);
    }
  };

  const handleQueryChange = (e) => {
    setQueryData({ ...queryData, [e.target.name]: e.target.value });
  };

  const handleSubmitQuery = async () => {
    if (!queryData.subject || !queryData.description) {
      toast.error("Please fill in all fields.");
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API_URI}/queries/`, queryData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("cine_userToken")}`
        }
      });
      toast.success("Query raised successfully!");
      setShowQueryModal(false);
      setQueryData({ subject: "", description: "" });
      setActiveTab("queries");
    } catch (error) {
      toast.error("Failed to raise query.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URI}/user/profile-image`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("cine_userToken")}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      if (response.status === 200) {
        const newImage = response?.data?.data?.profileImage;
        setProfileData((prev) => ({
          ...prev,
          profileImage: newImage
        }));
        toast.success("Profile image updated!");
      } else {
        toast.error("Failed to upload profile image.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const memberSince = useMemo(() => {
    if (!profileData?.createdAt) {
      return "Recently joined";
    }

    const joinedDate = new Date(profileData.createdAt);
    if (Number.isNaN(joinedDate.getTime())) {
      return "Recently joined";
    }

    return `${joinedDate.toLocaleString("default", {
      month: "long"
    })} ${joinedDate.getFullYear()}`;
  }, [profileData?.createdAt]);

  const profileInitials = useMemo(() => {
    const safeName = profileData?.name?.trim();
    if (!safeName) {
      return "CC";
    }

    return safeName
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("");
  }, [profileData?.name]);

  const profileHighlights = [
    {
      label: "Mobile",
      value: profileData?.mobile || "Add your mobile"
    },
    {
      label: "Email",
      value: profileData?.email || "Add your email"
    }
  ];

  return (
    <>
      <Navbar />

      <div className="profile-page-shell">
        <div className="profile-page-orb profile-page-orb--one" />
        <div className="profile-page-orb profile-page-orb--two" />

        <div className="profile-page-layout">
          <aside className="profile-identity-card">
            <div className="profile-identity-card__glow" />
            <div className="profile-identity-card__member-badge">
              <span>Member Since</span>
              <strong>{memberSince}</strong>
            </div>

            <div className="profile-identity-card__avatar-wrap">
              <div className="profile-identity-card__avatar-ring">
                {profileData?.profileImage ? (
                  <Image
                    src={profileData.profileImage}
                    roundedCircle
                    className="profile-identity-card__avatar"
                  />
                ) : (
                  <div className="profile-identity-card__avatar profile-identity-card__avatar--fallback">
                    {profileInitials}
                  </div>
                )}
              </div>

              <button
                type="button"
                className="profile-identity-card__avatar-action"
                onClick={() => document.getElementById("profileImage").click()}
                aria-label="Upload profile image"
              >
                <i className="bi bi-pencil-fill" />
              </button>

              <Form.Group controlId="formFile" className="d-none">
                <Form.Control
                  type="file"
                  id="profileImage"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Form.Group>
            </div>

            <div className="profile-identity-card__copy">
              <p className="profile-kicker">Your Celebration Profile</p>
              <h1>{profileData?.name || "Your Name"}</h1>
              <p>
                Keep your details ready, revisit past bookings, and raise
                support queries without hunting through emails.
              </p>
            </div>

            <div className="profile-identity-card__stats">
              {profileHighlights.map((item) => (
                <div className="profile-identity-card__stat" key={item.label}>
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>

            <div className="profile-identity-card__actions">
              <Button
                className="profile-action-button profile-action-button--primary"
                onClick={() => setShowEditModal(true)}
              >
                Edit Profile
              </Button>
              <Button
                className="profile-action-button profile-action-button--secondary"
                onClick={() => setShowQueryModal(true)}
              >
                Raise a Query
              </Button>
            </div>
          </aside>

          <section className="profile-workspace">
            <div className="profile-workspace__hero">
              <div>
                <p className="profile-kicker">Dashboard</p>
                <h2>Everything about your celebrations</h2>
              </div>

              <div className="profile-workspace__hero-badges">
                <div className="profile-workspace__badge">
                  <span>Profile Status</span>
                  <strong>{loading ? "Updating..." : "Ready to book"}</strong>
                </div>
                <div className="profile-workspace__badge">
                  <span>Support</span>
                  <strong>Fast help when you need it</strong>
                </div>
              </div>
            </div>

            <div className="profile-tabbar">
              <div className="profile-tab-switcher" role="tablist">
                <button
                  type="button"
                  className={`profile-tab-switcher__button ${
                    activeTab === "bookings" ? "is-active" : ""
                  }`}
                  onClick={() => setActiveTab("bookings")}
                >
                  Booking History
                </button>
                <button
                  type="button"
                  className={`profile-tab-switcher__button ${
                    activeTab === "queries" ? "is-active" : ""
                  }`}
                  onClick={() => setActiveTab("queries")}
                >
                  Queries
                </button>
              </div>

              <div className="profile-support-actions">
                <a
                  href="tel:+919643055509"
                  className="profile-support-actions__button profile-support-actions__button--call"
                  aria-label="Call support"
                  title="Call support"
                >
                  <i className="bi bi-telephone-fill profile-support-actions__icon" />
                </a>
                <a
                  href="https://wa.me/919643055509"
                  target="_blank"
                  rel="noreferrer"
                  className="profile-support-actions__button profile-support-actions__button--whatsapp"
                  aria-label="Chat on WhatsApp"
                  title="Chat on WhatsApp"
                >
                  <i className="bi bi-whatsapp profile-support-actions__icon" />
                </a>
              </div>
            </div>

            <div className="profile-content-panel">
              {activeTab === "bookings" ? <BookingHistory /> : <UserQueries />}
            </div>
          </section>
        </div>
      </div>

      <Footer />

      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        dialogClassName="profile-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="profile-modal__form">
            <Form.Group className="mb-3" controlId="formName">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                name="name"
                value={profileData.name}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formMobile">
              <Form.Label>Mobile</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter mobile number"
                name="mobile"
                value={profileData.mobile}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-0" controlId="formEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                name="email"
                value={profileData.email}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="profile-action-button profile-action-button--secondary"
            onClick={() => setShowEditModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="profile-action-button profile-action-button--primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? <Loader /> : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showQueryModal}
        onHide={() => setShowQueryModal(false)}
        centered
        dialogClassName="profile-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Raise a Query</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form className="profile-modal__form">
            <Form.Group className="mb-3">
              <Form.Label>Subject</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter query subject"
                name="subject"
                value={queryData.subject}
                onChange={handleQueryChange}
              />
            </Form.Group>
            <Form.Group className="mb-0">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Describe your issue"
                name="description"
                value={queryData.description}
                onChange={handleQueryChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="profile-action-button profile-action-button--secondary"
            onClick={() => setShowQueryModal(false)}
          >
            Cancel
          </Button>
          <Button
            className="profile-action-button profile-action-button--primary"
            onClick={handleSubmitQuery}
            disabled={loading}
          >
            {loading ? <Loader /> : "Submit"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default Profile;
